import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';
import CampChatModal from './CampChatModal';
import CampGroupChatModal from './CampGroupChatModal';

const THEMES = {
  dark: {
    bg: '#111827',
    cardBg: '#1f2937',
    cardHoverBg: '#374151',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    text: '#e5e7eb',
    textMuted: '#9ca3af',
  },
  light: {
    bg: '#ffffff',
    cardBg: '#f3f4f6',
    cardHoverBg: '#e5e7eb',
    inputBg: 'rgba(0,0,0,0.05)',
    border: 'rgba(0,0,0,0.1)',
    text: '#1a1a1a',
    textMuted: '#6b7280',
  },
};

export default function CampChatListModal({ isOpen, onClose }) {
  const { walletAddress } = useCoin();
  const [rooms, setRooms] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedGroupChat, setSelectedGroupChat] = useState(null);
  const [theme] = useState(() => {
    const savedMode = localStorage.getItem('campThemeMode');
    const savedTheme = localStorage.getItem('campTheme');
    if (savedMode === 'system' || !savedMode) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return savedTheme || 'dark';
  });

  const colors = THEMES[theme];

  useEffect(() => {
    if (isOpen && walletAddress) {
      fetchRooms();
      fetchGroupChats();
    }
  }, [isOpen, walletAddress]);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('camp_chat_rooms')
        .select('*')
        .or(`user1_wallet.eq.${walletAddress},user2_wallet.eq.${walletAddress}`)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      setRooms(data || []);
    } catch (err) {
      console.error('Failed to fetch chat rooms:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupChats = async () => {
    try {
      const { data: memberData, error: memErr } = await supabase
        .from('camp_group_chat_members')
        .select('group_chat_id')
        .eq('wallet_address', walletAddress);
      if (memErr) throw memErr;

      if (!memberData || memberData.length === 0) {
        setGroupChats([]);
        return;
      }

      const chatIds = memberData.map(m => m.group_chat_id);
      const { data: chats, error: chatErr } = await supabase
        .from('camp_group_chats')
        .select('*')
        .in('id', chatIds)
        .order('last_message_at', { ascending: false, nullsFirst: false });
      if (chatErr) throw chatErr;

      const chatsWithCounts = await Promise.all(
        (chats || []).map(async (chat) => {
          const { count } = await supabase
            .from('camp_group_chat_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_chat_id', chat.id);
          return { ...chat, member_count: count || 0 };
        })
      );
      setGroupChats(chatsWithCounts);
    } catch (err) {
      console.error('Failed to fetch group chats:', err);
    }
  };

  const getOtherUser = (room) => {
    if (room.user1_wallet === walletAddress) {
      return { wallet: room.user2_wallet, nickname: room.user2_nickname || 'Anonymous' };
    }
    return { wallet: room.user1_wallet, nickname: room.user1_nickname || 'Anonymous' };
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  const handleOpenChat = (room) => {
    const other = getOtherUser(room);
    setSelectedRoom({
      targetWallet: other.wallet,
      targetNickname: other.nickname,
      postId: room.post_id,
      postTitle: room.post_title,
    });
  };

  if (!isOpen) return null;

  // If a group chat is selected, show group chat modal
  if (selectedGroupChat) {
    return (
      <CampGroupChatModal
        isOpen={true}
        onClose={() => {
          setSelectedGroupChat(null);
          fetchGroupChats();
        }}
        groupChatId={selectedGroupChat.id}
        postId={selectedGroupChat.post_id}
        postTitle={selectedGroupChat.name}
      />
    );
  }

  // If a chat is selected, show the chat modal
  if (selectedRoom) {
    return (
      <CampChatModal
        isOpen={true}
        onClose={() => {
          setSelectedRoom(null);
          fetchRooms();
        }}
        targetWallet={selectedRoom.targetWallet}
        targetNickname={selectedRoom.targetNickname}
        postId={selectedRoom.postId}
        postTitle={selectedRoom.postTitle}
      />
    );
  }

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.bg,
          borderRadius: 16,
          width: '100%',
          maxWidth: 430,
          height: '80vh',
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>
            Chats
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              fontSize: 22,
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {/* Chat Room List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 8,
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>
              Loading...
            </div>
          ) : rooms.length === 0 && groupChats.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40, fontSize: 13 }}>
              No chats yet
            </div>
          ) : (
            <>
            {/* Group Chats */}
            {groupChats.map((gc) => (
              <div
                key={`gc_${gc.id}`}
                onClick={() => setSelectedGroupChat(gc)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.cardBg}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {gc.member_count || 0}P
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                        Party Chat
                      </span>
                      <span style={{
                        fontSize: 9, color: '#22c55e',
                        background: 'rgba(34,197,94,0.15)',
                        padding: '1px 5px', borderRadius: 4,
                        fontWeight: 600,
                      }}>
                        GROUP
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: colors.textMuted, flexShrink: 0 }}>
                      {formatTime(gc.last_message_at || gc.created_at)}
                    </span>
                  </div>
                  {gc.name && (
                    <div style={{
                      fontSize: 11, color: '#667eea', marginBottom: 2,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {gc.name}
                    </div>
                  )}
                  {gc.last_message && (
                    <div style={{
                      fontSize: 12, color: colors.textMuted,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {gc.last_message}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* 1:1 Chats */}
            {rooms.map((room) => {
              const other = getOtherUser(room);
              return (
                <div
                  key={room.id}
                  onClick={() => handleOpenChat(room)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = colors.cardBg}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 16,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {(other.nickname || '?')[0].toUpperCase()}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                        {other.nickname}
                      </span>
                      <span style={{ fontSize: 11, color: colors.textMuted, flexShrink: 0 }}>
                        {formatTime(room.last_message_at || room.created_at)}
                      </span>
                    </div>
                    {(room.post_title || room.applied_class) && (
                      <div style={{
                        fontSize: 11, color: '#667eea', marginBottom: 2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {room.post_title && <span>{room.post_title}</span>}
                        {room.applied_class && (
                          <span style={{
                            background: 'rgba(102,126,234,0.15)',
                            padding: '1px 6px',
                            borderRadius: 6,
                            fontSize: 10,
                            flexShrink: 0,
                          }}>
                            {room.applied_class}
                          </span>
                        )}
                      </div>
                    )}
                    {room.last_message && (
                      <div style={{
                        fontSize: 12, color: colors.textMuted,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {room.last_message}
                      </div>
                    )}
                    {(room.user1_deleted || room.user2_deleted) && (
                      <div style={{
                        fontSize: 10, color: '#f44336', marginTop: 2,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        {room.user1_deleted && room.user2_deleted
                          ? 'Both requested deletion'
                          : `Delete requested (${
                              (room.user1_wallet === walletAddress ? room.user1_deleted : room.user2_deleted) ? 'You' : other.nickname
                            })`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
            }
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
